import React from "react";
import { Box } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import { Sun03Icon, Moon02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { useColorScheme, ColorScheme } from "context/ColorSchemeContext";
import { Inline } from "components/layout/reusableLayouts";

export type ThemeMode = ColorScheme;

export interface ModeSwitchButtonProps {
  /**
   * Override the selected mode. Defaults to the app-wide color scheme from
   * `ColorSchemeContext`. Only pass this to drive the component manually
   * (e.g. in isolation outside the provider).
   */
  mode?: ThemeMode;
  /**
   * Override the change handler. Defaults to `setColorScheme` from
   * `ColorSchemeContext`, so the toggle switches the app theme out of the box.
   */
  onChange?: (mode: ThemeMode) => void;
}

const trackSx: SxProps<Theme> = {
  display: "flex",
  width: "100%",
  maxHeight: 38,
  padding: "var(--space-2)",
  border: "var(--border-width-1) solid var(--color-border)",
  borderRadius: "var(--radius-full)",
  backgroundColor: "var(--color-bg-surface)",
};

const makeSegmentSx = (selected: boolean): SxProps<Theme> => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flex: 1,
  height: 28,
  padding: 0,
  border: "none",
  borderRadius: "var(--radius-full)",
  backgroundColor: selected
    ? "var(--color-bg-elevated)"
    : "var(--color-bg-surface)",
  color: selected ? "var(--color-text-primary)" : "var(--color-text-tertiary)",
  cursor: "pointer",
  transition: "background-color 0.15s ease, color 0.15s ease",
  "&:hover": {
    color: "var(--color-text-primary)",
  },
});

/**
 * Light/dark pill, wired to `ColorSchemeContext` unless `mode`/`onChange` are given.
 *
 * Never gate this on branding: the scheme is the user's, over platform and
 * company colours alike.
 */
const ModeSwitchButton: React.FC<ModeSwitchButtonProps> = ({
  mode,
  onChange,
}) => {
  const { colorScheme, setColorScheme } = useColorScheme();
  const selected = mode ?? colorScheme;
  const handleSelect = onChange ?? setColorScheme;

  return (
    <Inline
      role="group"
      aria-label="Theme mode"
      gap="var(--space-2)"
      wrap={false}
      sx={trackSx}
    >
      <Box
        component="button"
        type="button"
        aria-pressed={selected === "light"}
        aria-label="Light mode"
        onClick={() => handleSelect("light")}
        sx={makeSegmentSx(selected === "light")}
      >
        <HugeiconsIcon icon={Sun03Icon} size={14} color="currentColor" />
      </Box>
      <Box
        component="button"
        type="button"
        aria-pressed={selected === "dark"}
        aria-label="Dark mode"
        onClick={() => handleSelect("dark")}
        sx={makeSegmentSx(selected === "dark")}
      >
        <HugeiconsIcon icon={Moon02Icon} size={14} color="currentColor" />
      </Box>
    </Inline>
  );
};

export default ModeSwitchButton;
